"use client";

import { useEffect } from "react";

/**
 * CursorTracker — O(1) optimized pointer tracker.
 * Updates local CSS properties (--mx, --my) only on the nearest hovered
 * specular surface (.lovable-card/.vortixy-surface/.magnetic).
 */
export function CursorTracker() {
  useEffect(() => {
    // Sin hover no hay cursor que seguir (táctil): fuera listeners y rAF.
    if (window.matchMedia?.("(hover: none)").matches) return;
    // 2026-09-19 FASE 2 FIXER reduced-motion (WCAG 2.2 AA 2.3.3 vestibular +
    // research 2026 vestibular-disorders: el specular que persigue el cursor
    // es movimiento no esencial — con reduce queda estático; el brillo base
    // del CSS se conserva, solo cesa el seguimiento).
    // 2026-09-25 FASE 3 design-chat-tokens (research 2026 WCAG 2.2 2.3.3:
    // el check único en mount no cubría el cambio mid-sesión — listener change
    // que retira el pointermove si el usuario activa reduce-motion después).
    const reduceMq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (reduceMq?.matches) return;
    // 2026-09-21 FASE 2 FIXER perf (free-tier CPU + INP <200ms en /chat:
    // el tracker es decorativo del landing — en el workspace (cualquier
    // prefijo de locale + /chat) no hay superficies specular visibles y el
    // pointermove global + rAF compite con el composer; fuera listeners).
    // research 2026: los listeners globales pasivos siguen costando dispatch
    // por evento en la vista más interactiva — gatear por ruta es el fix.
    const path = window.location?.pathname ?? "";
    if (/(^|\/)chat(\/|$)/.test(path)) return;
    let rafId = 0;
    let lastEvent: PointerEvent | null = null;
    const handlePointerMove = (e: PointerEvent) => {
      // Pestaña oculta: rAF ya se pausa solo, pero se evita encolar trabajo.
      if (document.hidden) return;
      lastEvent = e;
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        if (document.hidden) return;
        if (!lastEvent) return;
        const target = (lastEvent.target as HTMLElement).closest(
          // 2026-09-13 canónico animaciones: el specular sigue a las
          // superficies vivas del sistema. Las legacy se purgaron de
          // globals.css (0 usos) y salen del selector.
          ".status-bento-card, .status-hero, .lovable-card, .vortixy-surface, .magnetic",
        );
        if (target) {
          const rect = target.getBoundingClientRect();
          const x = lastEvent.clientX - rect.left;
          const y = lastEvent.clientY - rect.top;
          (target as HTMLElement).style.setProperty("--mx", `${x}px`);
          (target as HTMLElement).style.setProperty("--my", `${y}px`);
        }
      });
    };

    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    // Si el usuario activa reduce-motion mid-sesión, se retira el seguimiento.
    const handleReduceChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        window.removeEventListener("pointermove", handlePointerMove);
        if (rafId) window.cancelAnimationFrame(rafId);
      }
    };
    reduceMq?.addEventListener?.("change", handleReduceChange);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      reduceMq?.removeEventListener?.("change", handleReduceChange);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  return null;
}
